import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  buildQueryString,
  exportFile,
  formatNumber,
  showError,
  showSuccess,
  trimData,
} from "../../../../utils/utilities";
import { useFormik } from "formik";
import { defaultTo, get, indexOf, isEmpty, lowerCase, toLower } from "lodash";
import { useTranslation } from "react-i18next";
import { fileFormatXextension, langDate, mapMonths } from "../../../../utils/properties";
import Filter from "../../../../components/functional_components/Filter/Filter";
import { setFilters } from "../../../../redux/ducks/Filters";
import { Collapse, Row, Col, Modal, Tooltip, Spin } from "antd";
import KickOff from "../../../../services/api/KickOff";
import {
  DownloadOutlined,
  ReloadOutlined,
  CopyOutlined,
  SaveOutlined,
  CheckOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  UploadOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import Button from "../../../../components/functional_components/Button/PFButton";
import { setLoading } from "../../../../redux/ducks/Loading";
import SummaryTable from "./Table/SummaryTable";
import SummaryTotalTable from "./Table/SummaryTotalTable";
import TotalTable from "./Table/TotalTable";
import WPTable from "./Table/WPTable";
import BillingPlan from "./Table/BillingPlan";
import BillingPlanInput from "./Table/BillingPlanInput";
import { initializeFormBilling, resetForm } from "../../../../redux/ducks/EditableTable";
import { setRefreshKickOff, setRefreshTotals } from "../../../../redux/ducks/ProjectEstimate";
import dayjs from "dayjs";
import ProjectEstimate from "../../../../services/api/ProjectEstimate";
import Dragger from "antd/es/upload/Dragger";
import VopTable from "./Table/VopTable";

const KickOffScreen = (props) => {
  const { t } = useTranslation();
  const { confirm } = Modal;
  const dispatch = useDispatch();
  const currentDate = dayjs().format("YYYY");
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const isDirtyCustomForm = useSelector((state) => get(state.EditableTableDuck, "isDirty", {}));
  const isDirtyCustomFormBilling = useSelector((state) => get(state.EditableTableDuck, "isDirtyBilling", {}));
  const refreshKickOff = useSelector((state) => state.ProjectEstimateDuck.refreshKickOff);
  const customFormBilling = useSelector((state) => get(state.EditableTableDuck, "customFormBilling", {}));

  const [state, setState] = useState({
    data: [],
    detail: {},
    loading: false,
    resetLoading: false,
    copyLoading: false,
    downloadLoading: false,
    filterLoading: false,
    activePanel: [],
    dataContentPanel: [],
    dataContentTotal: [],
    dataContentBillingPlan: [],
    totalProject: 0,
    totalBilling: 0,
    fileList: [],
    uploadModal: { visible: false, wpId: "" },
    summaryTableVop: [],
    workPackageTableVop: [],
  });

  useEffect(() => {
    if (refreshKickOff === null) {
      const queryString = buildQueryString({}, { year: currentDate, allPages: true });
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    } else if (refreshKickOff === true) {
      initWpData();
    }
  }, [refreshKickOff]);

  const initWpData = async () => {
    const wpData = await getWpData(state.activePanel);
    const wpDataVop = await getWpVopData(state.activePanel);
    setState((prevState) => ({
      ...prevState,
      dataContentPanel: get(wpData.kickOffTable, "data.costEstimationKickoffValueView", []),
      dataContentTotal: [get(wpData.kickOffTableTotal, "data", [])],
      totalProject: get(wpData.kickOffTableTotal, "data.totalAmountValues.columnValueMap.TOT", 0),
      workPackageTableVop: [get(wpDataVop, "data.columnValueMap", {})],
    }));
    dispatch(setRefreshKickOff(false));
    dispatch(setRefreshTotals(true));
  };

  const dataList = async (queryString) => {
    dispatch(setLoading(true));
    const workPackage = await KickOff.KickOffWorkPackage(props.id, queryString);
    if (!workPackage.error) {
      setState((prevState) => ({ ...prevState, data: workPackage.data }));
    } else showError(get(workPackage, "error.messageCode", "ER000"));
    dispatch(setLoading(false));
  };

  const formikFilter = useFormik({
    initialValues: { year: dayjs(currentDate, "YYYY") },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.year) errors.year = "Required!";
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      const queryString = buildQueryString(filters.obj, { year: dayjs(values.year, "YYYY").format("YYYY") });
      dataList(queryString.string);
      dispatch(setFilters(queryString));
      setState((prevState) => ({
        ...prevState,
        dataContentPanel: [],
        dataContentTotal: [],
        activePanel: [],
        totalProject: 0,
        totalBilling: 0,
      }));
    },
  });

  const fieldsFilter = [
    {
      type: "year",
      isRequired: true,
      id: "year",
      name: "year",
      format: "YYYY",
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("projectEstimate.filter.year"),
      value: formikFilter.values.year,
      onChange: (date) => formikFilter.setFieldValue("year", date),
    },
  ];

  const resetFilter = () => {
    const queryString = buildQueryString({}, { year: currentDate, allPages: true });
    dispatch(setFilters(queryString));
    dataList(queryString.string);
    setState((prevState) => ({ ...prevState, activePanel: [] }));
  };

  const getWpData = async (key) => {
    const wp = state.data.find((obj) => obj.id == key);
    let kickOffTable = {};
    let kickOffTableTotal = {};
    // detail
    const detail = await KickOff.KickOffWorkPackageDetail(props.id, wp.id, filters.obj.year);
    if (!detail.error) setState((prevState) => ({ ...prevState, detail: detail.data }));
    else showError(get(detail, "error.messageCode", "ER000"));
    // data
    kickOffTable = await KickOff.KickOffWorkPackageTable(props.id, wp.id, filters.obj.year);
    if (!kickOffTable.error && kickOffTable.data) {
      kickOffTable.data.costEstimationKickoffValueView.forEach((wp, i) => {
        wp.key = i;
      });
    } else showError(get(kickOffTable, "error.messageCode", "ER000"));
    // total data
    kickOffTableTotal = await KickOff.KickOffWorkPackageTableTotal(props.id, wp.id, filters.obj.year);
    if (!kickOffTableTotal.error && kickOffTableTotal.data) kickOffTableTotal.data.key = 0;
    else showError(get(kickOffTableTotal, "error.messageCode", "ER000"));
    return {
      kickOffTable,
      kickOffTableTotal,
    };
  };

  const getWpDataBilling = async (key) => {
    const wp = state.data.find((obj) => obj.id == key);
    let kickOffTableBillingPlan = {};
    // Billing plan
    kickOffTableBillingPlan = await KickOff.KickOffWorkPackageTableBillingPlan(props.id, wp.id, filters.obj.year);
    if (!kickOffTableBillingPlan.error && kickOffTableBillingPlan.data) {
      let customFormB = [];
      // save value in custom form
      Object.keys(kickOffTableBillingPlan.data.columnValueMap).forEach((m) => {
        if (mapMonths[lowerCase(m)]) {
          customFormB.push({
            month: mapMonths[lowerCase(m)],
            amount: kickOffTableBillingPlan.data.columnValueMap[m],
          });
        }
      });
      dispatch(initializeFormBilling(customFormB));
      kickOffTableBillingPlan.data.key = 0;
    } else {
      showError(get(kickOffTableBillingPlan, "error.messageCode", "ER000"));
    }
    return kickOffTableBillingPlan;
  };

  const getWpVopData = async (key) => {
    const wp = state.data.find((obj) => obj.id == key);
    const workPackageTableVop = await KickOff.kickOffWpVop(props.id, wp.id, filters.obj.year);
    if (workPackageTableVop.error) {
      showError(get(workPackageTableVop, "error.messageCode", "ER000"));
    } else {
      workPackageTableVop.data.columnValueMap.key = "vop";
    }
    return workPackageTableVop;
  };

  const onChangeCollapse = async (key) => {
    if (!key) {
      setState((prevState) => ({
        ...prevState,
        dataContentPanel: [],
        dataContentTotal: [],
        dataContentBillingPlan: [],
        summaryTableVop: [],
        workPackageTableVop: [],
        totalProject: 0,
        totalBilling: 0,
      }));
      return;
    }
    key = key[0];
    dispatch(resetForm()); // Init values of editable table
    dispatch(setLoading(true));
    setState((prevState) => ({ ...prevState, activePanel: key }));
    let kickOffTable = {};
    let kickOffTableTotal = {};
    let kickOffTableBillingPlan = {};
    let summaryTableVop = {};
    let workPackageTableVop = {};
    if (key === "summary") {
      // Summary detail
      const detail = await KickOff.KickOffSummmaryDetail(props.id, filters.obj.year);
      if (!detail.error) setState((prevState) => ({ ...prevState, detail: detail.data }));
      else showError(get(detail, "error.messageCode", "ER000"));
      // summary data
      kickOffTable = await KickOff.KickOffTableSummary(props.id, filters.obj.year);
      if (!kickOffTable.error && kickOffTable.data) {
        kickOffTable.data.costEstimationKickoffValueView.forEach((wp, i) => {
          wp.key = i;
        });
      } else showError(get(kickOffTable, "error.messageCode", "ER000"));
      // summary total data
      kickOffTableTotal = await KickOff.KickOffTableTotalSummary(props.id, filters.obj.year);
      if (!kickOffTableTotal.error && kickOffTableTotal.data) {
        kickOffTableTotal.data.key = 0; // This is necessary to remove warning in console for unique key row
      } else showError(get(kickOffTableTotal, "error.messageCode", "ER000"));
      // vop data
      summaryTableVop = await KickOff.kickOffSummaryVop(props.id, filters.obj.year);
      if (summaryTableVop.error) {
        showError(get(summaryTableVop, "error.messageCode", "ER000"));
      }
    } else if (key === "summaryWip") {
      // Summary wip detail
      const detail = await KickOff.KickOffSummmaryWipDetail(props.id, filters.obj.year);
      if (!detail.error) setState((prevState) => ({ ...prevState, detail: detail.data }));
      else showError(get(detail, "error.messageCode", "ER000"));
      // summary wip data
      kickOffTable = await KickOff.KickOffTableSummaryWip(props.id, filters.obj.year);
      if (!kickOffTable.error && kickOffTable.data) {
        kickOffTable.data.costEstimationKickoffValueView.forEach((wp, i) => {
          wp.key = i;
        });
      } else showError(get(kickOffTable, "error.messageCode", "ER000"));
      // summary wip total data
      kickOffTableTotal = await KickOff.KickOffTableTotalSummaryWip(props.id, filters.obj.year);
      if (!kickOffTableTotal.error && kickOffTableTotal.data) {
        kickOffTableTotal.data.key = 0; // This is necessary to remove warning in console for unique key row
      } else showError(get(kickOffTableTotal, "error.messageCode", "ER000"));
      // vop data
      summaryTableVop = await KickOff.kickOffSummaryWipVop(props.id, filters.obj.year);
      if (summaryTableVop.error) {
        showError(get(summaryTableVop, "error.messageCode", "ER000"));
      }
    } else {
      const wp = state.data.find((obj) => obj.id == key);
      if (wp) {
        const wpData = await getWpData(key);
        const wpDataBilling = await getWpDataBilling(key);
        const wpDataVop = await getWpVopData(key);
        // data
        kickOffTable = wpData.kickOffTable;
        // total data
        kickOffTableTotal = wpData.kickOffTableTotal;
        // Billing plan
        kickOffTableBillingPlan = wpDataBilling;
        // vop data
        workPackageTableVop = wpDataVop;
      }
    }
    setState((prevState) => ({
      ...prevState,
      dataContentPanel: get(kickOffTable, "data.costEstimationKickoffValueView", []),
      dataContentTotal: [get(kickOffTableTotal, "data", [])],
      dataContentBillingPlan: [get(kickOffTableBillingPlan, "data", [])],
      totalProject: get(kickOffTableTotal, "data.totalAmountValues.columnValueMap.TOT", 0),
      totalBilling: get(kickOffTableBillingPlan, "data.columnValueMap.TOT", 0),
      summaryTableVop: [get(summaryTableVop, "data.columnValueMap", {})],
      workPackageTableVop: [get(workPackageTableVop, "data.columnValueMap", {})],
    }));

    dispatch(setLoading(false));
  };

  const handleDownload =
    (type, id = null) =>
    async () => {
      setState((prevState) => ({ ...prevState, downloadLoading: true }));
      let file = null;
      if (type === "summary") file = await KickOff.KickOffDownloadSummary(props.id, filters.obj.year);
      else file = await KickOff.KickOffDownloadWP(props.id, id, filters.obj.year);
      if (!file.error) {
        showSuccess();
        const blob = new Blob([file]);
        exportFile(blob, `${type !== "summary" ? type + "_" : type}${id ? id : ""}.csv`);
      } else showError(get(file, "error.messageCode", "ER000"));

      setState((prevState) => ({ ...prevState, downloadLoading: false }));
    };

  const handleCopy = (wpId) => () => {
    const wp = state.data.find((obj) => obj.id == wpId);
    confirm({
      title: `${t("projectEstimate.detail.tabs.kickOff.ensureCopy1")} WP${wp.counter} ${t(
        "projectEstimate.detail.tabs.kickOff.ensureCopy2"
      )} WP${wp.counter}?`,
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        setState((prevState) => ({ ...prevState, copyLoading: true }));
        const response = await KickOff.KickOffWorkPackageCopy(props.id, wpId, filters.obj.year);
        if (!response.error) {
          dispatch(resetForm());
          const wpData = await getWpData(wpId);
          const wpDataBilling = await getWpDataBilling(wpId);
          const wpDataVop = await getWpVopData(wpId);
          setState((prevState) => ({
            ...prevState,
            dataContentPanel: get(wpData.kickOffTable, "data.costEstimationKickoffValueView", []),
            dataContentTotal: [get(wpData.kickOffTableTotal, "data", [])],
            dataContentBillingPlan: [get(wpDataBilling, "data", [])],
            totalProject: get(wpData.kickOffTableTotal, "data.totalAmountValues.columnValueMap.TOT", 0),
            totalBilling: get(wpDataBilling, "data.columnValueMap.TOT", 0),
            workPackageTableVop: [get(wpDataVop, "data.columnValueMap", {})],
          }));
          showSuccess();
        } else {
          showError(get(response, "error.messageCode", "ER000"));
        }
        setState((prevState) => ({ ...prevState, copyLoading: false }));
      },
      onCancel() {},
    });
  };

  const handleReset = (wpId) => () => {
    confirm({
      title: t("projectEstimate.detail.tabs.kickOff.ensureReset"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        setState((prevState) => ({ ...prevState, resetLoading: true }));
        const response = await KickOff.resetProjectEstimateKickOffTableWP(props.id, wpId, filters.obj.year);
        if (!response.error) {
          dispatch(resetForm());
          const wpData = await getWpData(wpId);
          const wpDataBilling = await getWpDataBilling(wpId);
          const wpDataVop = await getWpVopData(wpId);
          setState((prevState) => ({
            ...prevState,
            dataContentPanel: get(wpData.kickOffTable, "data.costEstimationKickoffValueView", []),
            dataContentTotal: [get(wpData.kickOffTableTotal, "data", [])],
            dataContentBillingPlan: [get(wpDataBilling, "data", [])],
            totalProject: get(wpData.kickOffTableTotal, "data.totalAmountValues.columnValueMap.TOT", 0),
            totalBilling: get(wpDataBilling, "data.columnValueMap.TOT", 0),
            workPackageTableVop: [get(wpDataVop, "data.columnValueMap", {})],
          }));
        } else showError(get(response, "error.messageCode", "ER000"));
        setState((prevState) => ({ ...prevState, resetLoading: false }));
      },
      onCancel() {},
    });
  };

  const handleSave = (wpId) => async () => {
    dispatch(setLoading(true));
    const responseBilling = await KickOff.saveKickOffWorkPackageTableBillingPlan(
      props.id,
      wpId,
      filters.obj.year,
      customFormBilling
    );
    if (!responseBilling.error) {
      showSuccess();
      dispatch(resetForm());
      const wpData = await getWpData(wpId);
      const wpDataBilling = await getWpDataBilling(wpId);
      const wpDataVop = await getWpVopData(wpId);
      setState((prevState) => ({
        ...prevState,
        dataContentPanel: get(wpData.kickOffTable, "data.costEstimationKickoffValueView", []),
        dataContentTotal: [get(wpData.kickOffTableTotal, "data", [])],
        totalProject: get(wpData.kickOffTableTotal, "data.totalAmountValues.columnValueMap.TOT", 0),
        dataContentBillingPlan: [get(wpDataBilling, "data", [])],
        totalBilling: get(wpDataBilling, "data.columnValueMap.TOT", 0),
        workPackageTableVop: [get(wpDataVop, "data.columnValueMap", {})],
      }));
    } else {
      showError(get(responseBilling, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const handleRelease = (wpId) => () => {
    confirm({
      title: t("projectEstimate.detail.tabs.kickOff.ensureApprove"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const response = await KickOff.confirmProjectEstimateKickOffTableWP(props.id, wpId, filters.obj.year);
        if (!response.error) {
          dataList(filters.string);
          setState((prevState) => ({ ...prevState, activePanel: [] }));
          showSuccess();
        } else showError(get(response, "error.messageCode", "ER000"));
      },
      onCancel() {},
    });
  };

  const checkCheckoutKickOff = () => !(state.totalProject === 0 && state.totalBilling === 0);

  const checkshowAlert = () => (state.totalProject === 0 ? state.totalBilling !== 0 : state.totalBilling === 0);

  const renderSummaryContent = (isWip = false) => {
    return (
      <>
        <Row gutter={24}>
          <Col lg={8} md={8} xs={24} sm={8}>
            <Row>
              <label className="fw-bold  mb-10 mt-10">{t("projectEstimate.detail.tabs.kickOff.revenues")}</label>
            </Row>
            <Row>
              <Col span={20} className="mb-1em right">
                <div className="boxValue right">
                  {formatNumber({
                    value: defaultTo(state.detail.billingPlanAmount, "0"),
                    lang: currentLanguage,
                    suffix: "EUR",
                    decimalScale: 0,
                  })}
                </div>
              </Col>
            </Row>
          </Col>

          <Col lg={8} md={8} xs={24} sm={8}>
            <Row>
              <label className="fw-bold  mb-10 mt-10">{t("projectEstimate.detail.tabs.kickOff.costs")}</label>
            </Row>
            <Row>
              <Col span={20} className="mb-1em right">
                <div className="boxValue right">
                  {formatNumber({
                    value: defaultTo(state.detail.kickOffAmount, "0"),
                    lang: currentLanguage,
                    suffix: "EUR",
                    decimalScale: 0,
                  })}
                </div>
              </Col>
            </Row>
          </Col>

          <Col lg={8} md={8} xs={24} sm={8}>
            <Row>
              <label className="fw-bold  mb-10 mt-10">{t("projectEstimate.detail.tabs.kickOff.margin")}</label>
            </Row>
            <Row>
              <Col span={20} className="mb-1em right">
                <div className="boxValue right">
                  {formatNumber({
                    value: defaultTo(state.detail.gcmPercentage, "0"),
                    lang: currentLanguage,
                    suffix: "PERCENT",
                    decimalScale: 1,
                  })}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>

        {!isWip && (
          <Row type="flex" align="middle" justify="end" className="mb-10">
            <Button
              isLoading={state.downloadLoading}
              currentIcon={<DownloadOutlined className="icon-medium" />}
              clickCallback={handleDownload("summary")}
            />
          </Row>
        )}

        <SummaryTable isWip={isWip} dataContentSummary={state.dataContentPanel} id={props.id} whereIam={"kickOff"} />
        <SummaryTotalTable dataContentTotal={state.dataContentTotal} id={props.id} />
        <VopTable vopData={state.summaryTableVop} />
      </>
    );
  };

  const openUploadModal = (wpId) => () => {
    setState((prevState) => ({
      ...prevState,
      uploadModal: { visible: true, wpId },
    }));
  };

  const renderWpContent = (item) => {
    return (
      <>
        <Row type="flex" align="middle" justify="end">
          <Col lg={8} md={8} xs={24} sm={8}>
            <Row>
              <label className="fw-bold  mb-10 mt-10">{t("projectEstimate.detail.tabs.kickOff.revenues")}</label>
            </Row>
            <Row>
              <Col span={20} className="mb-1em right">
                <div className="boxValue right">
                  {formatNumber({
                    value: defaultTo(state.detail.billingPlanAmount, "0"),
                    lang: currentLanguage,
                    suffix: "EUR",
                    decimalScale: 0,
                  })}
                </div>
              </Col>
            </Row>
          </Col>

          <Col lg={8} md={8} xs={24} sm={8}>
            <Row>
              <label className="fw-bold  mb-10 mt-10">{t("projectEstimate.detail.tabs.kickOff.costs")}</label>
            </Row>
            <Row>
              <Col span={20} className="mb-1em right">
                <div className="boxValue right">
                  {formatNumber({
                    value: defaultTo(state.detail.kickOffAmount, "0"),
                    lang: currentLanguage,
                    suffix: "EUR",
                    decimalScale: 0,
                  })}
                </div>
              </Col>
            </Row>
          </Col>

          <Col lg={8} md={8} xs={24} sm={8}>
            <Row>
              <label className="fw-bold  mb-10 mt-10">{t("projectEstimate.detail.tabs.kickOff.margin")}</label>
            </Row>
            <Row>
              <Col span={20} className="mb-1em right">
                <div className="boxValue right">
                  {formatNumber({
                    value: defaultTo(state.detail.gcmPercentage, "0"),
                    lang: currentLanguage,
                    suffix: "PERCENT",
                    decimalScale: 1,
                  })}
                </div>
              </Col>
            </Row>
          </Col>
          <Row type="flex" align="middle" justify="end" className="mb-10">
            {item.status === "OPEN" && (
              <>
                <Button
                  isDisabled={!userInfo.canEditKickOff}
                  isLoading={state.resetLoading}
                  currentIcon={<ReloadOutlined className="icon-medium" />}
                  clickCallback={handleReset(item.id)}
                  className={"ml-10 mt-10 mb-10"}
                />
                <Button
                  isDisabled={!userInfo.canEditKickOff}
                  isLoading={state.copyLoading}
                  currentIcon={<CopyOutlined className="icon-medium" />}
                  clickCallback={handleCopy(item.id)}
                  className={"ml-10 mt-10 mb-10"}
                />
                <Button
                  isDisabled={!userInfo.canEditCostEstimation}
                  className="ml-10"
                  currentIcon={<UploadOutlined className="icon-medium" />}
                  clickCallback={openUploadModal(item.id)}
                />
              </>
            )}
            <Button
              isLoading={state.downloadLoading}
              currentIcon={<DownloadOutlined className="icon-medium" />}
              clickCallback={handleDownload(item.name, item.id)}
              className={"ml-10 mt-10 mb-10"}
            />
          </Row>
        </Row>
        <WPTable
          dataContentWP={state.dataContentPanel}
          projectId={props.id}
          WpId={item.id}
          status={item.status}
          type="kick-off"
        />
        <TotalTable dataContentTotal={state.dataContentTotal} id={props.id} />
        {item.status === "RELEASED" && <BillingPlan dataContentBillingPlan={state.dataContentBillingPlan} />}
        {item.status === "OPEN" && (
          <>
            <BillingPlanInput dataContentBillingPlan={state.dataContentBillingPlan} wp={item.id} type="kick-off" />
            <Row type="flex" align="middle" justify="end" className="mb-10 mt-10">
              <Tooltip title={t("projectEstimate.detail.tabs.kickOff.alertValues")} key="Warning">
                <WarningOutlined hidden={!checkshowAlert()} className="PFred icon-large" />
              </Tooltip>
              <Button
                isDisabled={
                  !(
                    userInfo.canEditKickOff &&
                    checkCheckoutKickOff() &&
                    !(isDirtyCustomForm || isDirtyCustomFormBilling)
                  )
                }
                currentIcon={<CheckOutlined className="icon-medium" />}
                className="ml-10"
                clickCallback={handleRelease(item.id)}
              />
              <Button
                isDisabled={!(userInfo.canEditKickOff && isDirtyCustomFormBilling)}
                currentIcon={<SaveOutlined className="icon-medium" />}
                className="ml-10"
                clickCallback={handleSave(item.id)}
              />
            </Row>
          </>
        )}
        <VopTable vopData={state.workPackageTableVop} />
      </>
    );
  };

  const items = [
    {
      key: "summary",
      label: t("projectEstimate.detail.tabs.costEstimation.summary"),
      className: "mb-1em",
      children: renderSummaryContent(),
    },
    {
      key: "summaryWip",
      label: t("projectEstimate.detail.tabs.costEstimation.summaryWip"),
      className: "mb-1em",
      children: renderSummaryContent(true),
    },
    ...(state.data && state.data.length > 0
      ? state.data.map((item) => ({
          key: item.id,
          label: `WP${item.counter} | ${item.name} | ${item.status}`,
          className: "mb-1em",
          children: renderWpContent(item),
        }))
      : []),
  ];

  const formikDocument = useFormik({
    initialValues: {
      file: "",
    },
    validate: (values) => {
      let errors = {};
      if (!values.file) {
        // file is required only on new, in edit I cannot upload file and edit only description
        errors.file = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async () => {
      if (!isEmpty(state.fileList)) {
        dispatch(setLoading(true));
        const formData = new FormData();
        state.fileList.forEach((file) => {
          formData.append("file", file);
        });
        setState((prevState) => ({ ...prevState, loading: true }));
        const response = await ProjectEstimate.uploadProjectEstimateKickOff(
          props.id,
          state.uploadModal.wpId,
          filters.obj.year,
          formData
        );
        if (!response.error) {
          const queryString = buildQueryString(filters.obj, { year: currentDate });
          dispatch(setFilters(queryString));
          dispatch(resetForm());
          const wpData = await getWpData(state.uploadModal.wpId);
          const wpDataBilling = await getWpDataBilling(state.uploadModal.wpId);
          const wpDataVop = await getWpVopData(state.uploadModal.wpId);
          setState((prevState) => ({
            ...prevState,
            dataContentPanel: get(wpData.kickOffTable, "data.costEstimationKickoffValueView", []),
            dataContentTotal: [get(wpData.kickOffTableTotal, "data", [])],
            dataContentBillingPlan: [get(wpDataBilling, "data", [])],
            totalProject: get(wpData.kickOffTableTotal, "data.totalAmountValues.columnValueMap.TOT", 0),
            totalBilling: get(wpDataBilling, "data.columnValueMap.TOT", 0),
            workPackageTableVop: [get(wpDataVop, "data.columnValueMap", {})],
          }));
          closeUploadModal();
          showSuccess();
        } else {
          showError(get(response, "error.messageCode", "ER000"));
        }
        setState((prevState) => ({ ...prevState, loading: false, fileList: [] }));
        dispatch(setLoading(false));
      }
    },
  });

  const propsFile = {
    multiple: false,
    onRemove: (file) => {
      const index = state.fileList.indexOf(file);
      const newFileList = state.fileList.slice();
      newFileList.splice(index, 1);
      setState((prevState) => ({
        ...prevState,
        fileList: newFileList,
      }));
      formikDocument.setFieldValue("file", "");
    },
    beforeUpload: (file) => {
      let ckFileList = [];
      let ckFileValue = "";
      if (indexOf(fileFormatXextension["csv"], file.type) !== -1) {
        ((ckFileList = [file]), (ckFileValue = "ok"));
      } else {
        showError(t("projectEstimate.modal.errorFileFormat"));
      }
      setState((prevState) => ({
        ...prevState,
        fileList: ckFileList,
      }));
      formikDocument.setFieldValue("file", ckFileValue);
      return false;
    },
    fileList: state.fileList,
  };

  const closeUploadModal = () => {
    formikDocument.resetForm();
    setState((prevState) => ({
      ...prevState,
      uploadModal: { visible: false, wpId: "" },
      fileList: [],
    }));
  };

  return (
    <>
      <Filter
        formikForm={formikFilter}
        fields={fieldsFilter}
        loading={state.filterLoading}
        allInOneRow={false}
        onReset={resetFilter}
      />
      <Collapse
        items={items}
        accordion
        activeKey={state.activePanel}
        onChange={onChangeCollapse}
        destroyInactivePanel
      />
      <Modal
        width={700}
        title={<b>{t("projectEstimate.detail.tabs.preventiveForm.titleNewDocument")}</b>}
        open={state.uploadModal.visible}
        onCancel={closeUploadModal}
        footer={[
          <Button
            isLoading={state.loading}
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikDocument.submitForm}
            isDisabled={!(formikDocument.dirty && formikDocument.isValid)}
          />,
        ]}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 48]}>
          <Col span={24}>
            <Row type="flex" justify="center" align="center" className="mt-1em mb-10">
              <Col span={24}>
                <p>{t("projectEstimate.modal.allowedExtensions")} .csv</p>
              </Col>
              <Col span={24}>
                <Dragger {...propsFile}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">{t("projectEstimate.detail.tabs.preventiveForm.uploadText")}</p>
                </Dragger>
              </Col>
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default KickOffScreen;
